import {
    APP_INITIALIZER,
    ApplicationConfig,
    importProvidersFrom,
    provideZonelessChangeDetection,
} from '@angular/core';
import {
    InMemoryScrollingFeature,
    InMemoryScrollingOptions,
    provideRouter,
    withInMemoryScrolling,
} from '@angular/router';

import { routes } from './config/routes/app.routes';
import {
    provideClientHydration,
    withEventReplay,
} from '@angular/platform-browser';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { ConfirmLeaveGuard } from '@guards/confirm-leave.guard';
import { provideToastr } from 'ngx-toastr';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { CORE_PROVIDERS } from '@config/core.module';
import { provideLottieOptions } from 'ngx-lottie';
import { NgxSpinnerModule } from 'ngx-spinner';
import { LanguageService } from '@services/global/language.service';
import { InitService } from '@services/init/init.service';
import { VolioAuthInterceptor } from './interceptor/auth.interceptor';
import { LoggingInterceptor } from './interceptor/logging.interceptor';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { GoogleAnalyticsService } from '@services/analytics/google-analytics.service';

const scrollConfig: InMemoryScrollingOptions = {
    scrollPositionRestoration: 'enabled', // set là top thì khi back lại nó sẽ scroll lên đầu page, enabled thì back lại giữ nguyên vị trí cũ
    anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
    withInMemoryScrolling(scrollConfig);

export function initializeAppDetail(initService: InitService) {
    return () => {
        return initService.init();
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        LanguageService,
        provideRouter(routes, inMemoryScrollingFeature),
        provideClientHydration(withEventReplay()),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        provideToastr(),
        provideZonelessChangeDetection(),
        ConfirmLeaveGuard,
        CORE_PROVIDERS,
        importProvidersFrom([
            TranslateModule.forRoot(),
            NgxSpinnerModule.forRoot({ type: 'square-jelly-box' }),
        ]),
        provideTranslateHttpLoader({
            prefix: '../../assets/i18n/',
            suffix: '.json',
        }),
        provideLottieOptions({
            player: () => import('lottie-web'),
        }),
        providePrimeNG({
            theme: {
                preset: Aura,
            },
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppDetail,
            deps: [InitService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (analyticsService: GoogleAnalyticsService) => () =>
                analyticsService.initialize(),
            deps: [GoogleAnalyticsService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: VolioAuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoggingInterceptor,
            multi: true,
        },
    ],
};
